import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../context/AuthProvider';
import { can } from '../utils/access';

/**
 * Envoltorio de ruta que exige sesión y (opcionalmente) un permiso.
 *
 * En React Router v6 las guardas se expresan como componentes que envuelven al
 * elemento, no como rutas especiales. Conserva la URL solicitada en `?next=` y
 * en `state.from`, para que tras el login se vuelva exactamente a donde se
 * quería entrar.
 *
 * `permission` acepta varios: basta con tener uno. Se comprueba contra la lista
 * de permisos y nunca contra el rol, que es un paquete de permisos que el
 * servidor puede redefinir sin que esta pantalla se entere.
 */
export default function ProtectedRoute({ children, permission, redirect = '/login' }) {
  const { user, sessionReady } = useAuthContext();
  const location = useLocation();

  // Los permisos guardados en localStorage pueden estar caducados (un cambio
  // de rol, un catálogo de permisos distinto). Hasta que el servidor confirme
  // la sesión no se expulsa a nadie: si no, recargar dentro de una ruta
  // protegida echaba fuera a quien sí tenía acceso.
  if (!sessionReady) return null;

  const permissions = Array.isArray(permission) ? permission : (permission ? [permission] : []);

  const allowed = permissions.length ? can(user, ...permissions) : Boolean(user);

  if (allowed) return children;

  const intended = location.pathname + location.search;

  return (
    <Navigate
      to={`${redirect}?next=${encodeURIComponent(intended)}`}
      state={{ from: intended }}
      replace
    />
  );
}
